import { computed, ref } from 'vue'

import { defineStore } from 'pinia'

import { getAppTimeZone, setAppTimeZone } from '@/helpers/app-timezone-datetime.helper'
import { useGeneralService } from '@/services/general/useGeneralService'

/**
 * La zona se cachea en localStorage para que un refresco no pinte las fechas en
 * la zona de respaldo mientras responde la API.
 */
const TIME_ZONE_STORAGE_KEY = 'appTimeZoneHub'

const readCachedTimeZone = (): string | null => {
  try {
    return localStorage.getItem(TIME_ZONE_STORAGE_KEY)
  } catch {
    return null
  }
}

const writeCachedTimeZone = (value: string) => {
  try {
    localStorage.setItem(TIME_ZONE_STORAGE_KEY, value)
  } catch {
    // Modo privado o storage lleno: se vuelve a pedir en el proximo arranque.
  }
}

/**
 * Configuracion de instalacion que necesita toda la aplicacion. Hoy solo la
 * zona horaria, con la que se muestran las fechas que llegan en UTC.
 *
 * Solo lectura: la zona se edita desde la configuracion de voice o del
 * callcenter, sobre la misma fila de `generals`.
 */
export const useAppSettingsStore = defineStore('app-settings', () => {
  const generalService = useGeneralService()

  const timeZone = ref(getAppTimeZone())
  const isTimeZoneLoaded = ref(false)

  let pendingLoad: Promise<string> | null = null

  const applyTimeZone = (value: string | null | undefined, persist = true): string => {
    const applied = setAppTimeZone(value)
    timeZone.value = applied

    if (persist) {
      writeCachedTimeZone(applied)
    }

    return applied
  }

  const loadTimeZone = async (): Promise<string> => {
    try {
      const response = await generalService.getTimezone()

      if (!response?.timezone) {
        console.warn(
          `[app-settings] La API no devolvio zona horaria; se sigue usando ${timeZone.value}.`,
        )
      }

      applyTimeZone(response?.timezone)
      isTimeZoneLoaded.value = true
    } catch (error) {
      // No se propaga —sin zona no se puede pintar una sola fecha— pero se
      // avisa: en silencio, el fallo es indistinguible de que la configuracion
      // no se este aplicando.
      console.warn(
        `[app-settings] No se pudo leer la zona horaria; se sigue usando ${timeZone.value}.`,
        error,
      )
    }

    return timeZone.value
  }

  /** Carga la zona una sola vez por sesion; `force` la relee. */
  const ensureTimeZoneLoaded = async (force = false): Promise<string> => {
    if (isTimeZoneLoaded.value && !force) return timeZone.value
    if (pendingLoad && !force) return pendingLoad

    pendingLoad = loadTimeZone().finally(() => {
      pendingLoad = null
    })

    return pendingLoad
  }

  applyTimeZone(readCachedTimeZone(), false)

  return {
    timeZone: computed(() => timeZone.value),
    isTimeZoneLoaded: computed(() => isTimeZoneLoaded.value),
    ensureTimeZoneLoaded,
  }
})
